import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  createProjectRequestSchema,
  type CreateProjectRequest,
  type ProjectView,
} from "@portal/schemas";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Label,
  Textarea,
} from "@portal/ui";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { createProject } from "../../api/client";
import { useInvalidate } from "../../api/queries";
import { InlineError } from "../../components/InlineError";
import { Plus } from "lucide-react";

/**
 * プロジェクトの作成。既定は「新規プロジェクト」のボタンで開き、作ったプロジェクトの画面へ移る。
 * プルダウンから「新しいプロジェクトを作成」で開くときは open / onOpenChange で外から開き、
 * onCreated で作ったプロジェクトを受け取る(画面は移らない。2026-10-15)。
 */
export function CreateProjectDialog({
  open: openProp,
  onOpenChange,
  onCreated,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onCreated?: (project: ProjectView) => void;
} = {}) {
  const [innerOpen, setInnerOpen] = useState(false);
  const controlled = openProp !== undefined;
  const open = openProp ?? innerOpen;
  const setOpen = onOpenChange ?? setInnerOpen;
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const form = useForm<CreateProjectRequest>({
    resolver: zodResolver(createProjectRequestSchema),
    defaultValues: { name: "", code: "", description: "" },
  });
  const create = useMutation({
    mutationFn: createProject,
    onSuccess: async (res) => {
      await invalidate.projects();
      setOpen(false);
      form.reset();
      if (onCreated) onCreated(res.project);
      else await navigate({ to: "/projects/$projectId", params: { projectId: res.project.id } });
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!controlled && (
        <DialogTrigger asChild>
          <Button>
            <Plus />
            新規プロジェクト
          </Button>
        </DialogTrigger>
      )}
      <DialogContent>
        <form
          onSubmit={form.handleSubmit((v) => create.mutate(v))}
          noValidate
          className="flex flex-col gap-4"
        >
          <DialogHeader>
            <DialogTitle>プロジェクトを作成</DialogTitle>
            <DialogDescription>作成した人が最初のプロジェクト管理者になります。</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-name">プロジェクト名</Label>
            <Input id="project-name" {...form.register("name")} />
            {form.formState.errors.name && (
              <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-code">プロジェクトコード(任意)</Label>
            <Input id="project-code" {...form.register("code")} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-description">概要(任意)</Label>
            <Textarea id="project-description" rows={4} {...form.register("description")} />
          </div>
          <InlineError error={create.error} />
          <DialogFooter>
            <Button type="submit" disabled={create.isPending}>
              <Plus />
              {create.isPending ? "作成中…" : "作成"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
